import React, { useState, useRef, useEffect } from 'react';
import {
  GlobeAltIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
  ChartBarIcon,
  TableCellsIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  CalendarDaysIcon,
  ChevronDownIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

// Project badge colors
const PROJECT_COLORS = {
  'iot-dashboard': { bg: 'bg-blue-100', text: 'text-blue-700', label: 'IoT Dashboard' },
  'user-dashboard': { bg: 'bg-green-100', text: 'text-green-700', label: 'User Dashboard' },
  'marketing': { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Marketing' },
};

// Time range presets with labels
const TIME_RANGE_PRESETS = [
  { value: '1h', label: '1h', fullLabel: 'Last Hour' },
  { value: '6h', label: '6h', fullLabel: 'Last 6 Hours' },
  { value: '24h', label: '24h', fullLabel: 'Last 24 Hours' },
  { value: '2d', label: '2d', fullLabel: 'Last 2 Days' },
  { value: '7d', label: '7d', fullLabel: 'Last 7 Days' },
  { value: '14d', label: '14d', fullLabel: 'Last 2 Weeks' },
  { value: '30d', label: '30d', fullLabel: 'Last Month' },
];

/**
 * ProjectTab - Individual project selector button
 */
const ProjectTab = ({ project, count, isActive, onClick }) => {
  const colors = PROJECT_COLORS[project.id] || { bg: 'bg-gray-100', text: 'text-gray-700', label: project.name };

  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
        isActive
          ? `${colors.bg} ${colors.text} ring-2 ring-offset-1 ring-opacity-50`
          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      }`}
    >
      <span>{project.name}</span>
      {count > 0 && (
        <span className={`px-1.5 py-0.5 rounded-full text-xs ${isActive ? 'bg-white/60' : 'bg-gray-200'}`}>
          {count.toLocaleString()}
        </span>
      )}
    </button>
  );
};

/**
 * TimeRangeSelector - Dropdown with presets and custom date picker
 */
const TimeRangeSelector = ({ timeRange, onTimeRangeChange, customRange, onCustomRangeChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowCustom(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get display label
  const getDisplayLabel = () => {
    if (customRange?.start && customRange?.end) {
      const start = new Date(customRange.start);
      const end = new Date(customRange.end);
      return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    }
    const preset = TIME_RANGE_PRESETS.find(p => p.value === timeRange);
    return preset?.label || timeRange;
  };

  const handlePresetSelect = (value) => {
    onTimeRangeChange?.(value);
    onCustomRangeChange?.(null); // Clear custom range
    setIsOpen(false);
    setShowCustom(false);
  };

  const handleCustomApply = () => {
    if (customStart && customEnd) {
      onCustomRangeChange?.({
        start: new Date(customStart).toISOString(),
        end: new Date(customEnd + 'T23:59:59').toISOString()
      });
      onTimeRangeChange?.('custom');
      setIsOpen(false);
      setShowCustom(false);
    }
  };

  const clearCustomRange = () => {
    onCustomRangeChange?.(null);
    onTimeRangeChange?.('24h');
    setCustomStart('');
    setCustomEnd('');
  };

  const isCustomActive = customRange?.start && customRange?.end;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
          isCustomActive
            ? 'bg-indigo-100 text-indigo-700'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        }`}
      >
        <CalendarDaysIcon className="w-4 h-4" />
        <span>{getDisplayLabel()}</span>
        <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-xl shadow-lg border border-gray-200 z-50 overflow-hidden">
          {/* Presets */}
          {!showCustom && (
            <div className="p-2">
              <p className="text-xs font-medium text-gray-500 px-2 py-1 mb-1">Quick Select</p>
              <div className="grid grid-cols-4 gap-1">
                {TIME_RANGE_PRESETS.map((preset) => (
                  <button
                    key={preset.value}
                    onClick={() => handlePresetSelect(preset.value)}
                    className={`px-3 py-2 text-sm rounded-lg transition-all ${
                      timeRange === preset.value && !isCustomActive
                        ? 'bg-indigo-100 text-indigo-700 font-medium'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                    title={preset.fullLabel}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>

              <div className="border-t border-gray-100 mt-2 pt-2">
                <button
                  onClick={() => setShowCustom(true)}
                  className={`w-full flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg transition-all ${
                    isCustomActive
                      ? 'bg-indigo-100 text-indigo-700 font-medium'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <CalendarDaysIcon className="w-4 h-4" />
                  Custom Range
                  {isCustomActive && (
                    <span className="text-xs bg-indigo-200 px-1.5 py-0.5 rounded">Active</span>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Custom Date Picker */}
          {showCustom && (
            <div className="p-3">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium text-gray-700">Custom Range</p>
                <button
                  onClick={() => setShowCustom(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    max={customEnd || undefined}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">End Date</label>
                  <input
                    type="date"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    min={customStart || undefined}
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div className="flex gap-2 pt-1">
                  {isCustomActive && (
                    <button
                      onClick={clearCustomRange}
                      className="flex-1 px-3 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Clear
                    </button>
                  )}
                  <button
                    onClick={handleCustomApply}
                    disabled={!customStart || !customEnd}
                    className="flex-1 px-3 py-2 text-sm text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * NginxTrafficHeader - Unified header for both Charts and Table views
 */
const NginxTrafficHeader = ({
  // View state
  viewMode = 'charts',
  onToggleView,

  // Project selection
  projects = [],
  selectedProject = 'all',
  onProjectChange,
  projectCounts = {},
  totalCount = 0,

  // Time range
  timeRange = '24h',
  onTimeRangeChange,
  customRange = null,
  onCustomRangeChange,

  // Search & filters (for table view)
  searchTerm = '',
  onSearchChange,
  selectedStatus = 'all',
  onStatusChange,
  showFilters = false,

  // Actions
  onRefresh,
  onExport,
  loading = false,
  autoRefresh = false,
  onAutoRefreshToggle,

  // Timestamp
  lastUpdated = null,
}) => {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
      {/* Top Row: Title, View Toggle, Timestamp */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <GlobeAltIcon className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-800">Nginx Traffic</h1>
            {lastUpdated && (
              <p className="text-xs text-gray-500">
                Updated {lastUpdated.toLocaleTimeString()}
              </p>
            )}
          </div>
        </div>

        {/* View Toggle */}
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => onToggleView?.('charts')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                viewMode === 'charts'
                  ? 'bg-white text-indigo-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <ChartBarIcon className="w-4 h-4" />
              <span>Charts</span>
            </button>
            <button
              onClick={() => onToggleView?.('table')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                viewMode === 'table'
                  ? 'bg-white text-indigo-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <TableCellsIcon className="w-4 h-4" />
              <span>Table</span>
            </button>
          </div>
        </div>
      </div>

      {/* Middle Row: Project Tabs, Time Range, Auto-refresh */}
      <div className="flex flex-wrap items-center gap-2">
        {/* All Traffic Button */}
        <button
          onClick={() => onProjectChange?.('all')}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
            selectedProject === 'all'
              ? 'bg-gray-800 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          All Traffic
          {totalCount > 0 && (
            <span className={`px-1.5 py-0.5 rounded-full text-xs ${selectedProject === 'all' ? 'bg-white/20' : 'bg-gray-200'}`}>
              {totalCount.toLocaleString()}
            </span>
          )}
        </button>

        {/* Project Tabs */}
        {projects.map(project => (
          <ProjectTab
            key={project.id}
            project={project}
            count={projectCounts[project.id] || 0}
            isActive={selectedProject === project.id}
            onClick={() => onProjectChange?.(project.id)}
          />
        ))}

        <div className="flex-1" />

        {/* Time Range Selector */}
        <TimeRangeSelector
          timeRange={timeRange}
          onTimeRangeChange={onTimeRangeChange}
          customRange={customRange}
          onCustomRangeChange={onCustomRangeChange}
        />

        {/* Auto-refresh Toggle */}
        {onAutoRefreshToggle && (
          <button
            onClick={onAutoRefreshToggle}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
              autoRefresh
                ? 'bg-green-100 text-green-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <ArrowPathIcon className={`w-3.5 h-3.5 ${autoRefresh ? 'animate-spin' : ''}`} />
            {autoRefresh ? 'Live' : 'Auto'}
          </button>
        )}

        {/* Refresh Button */}
        <button
          onClick={onRefresh}
          disabled={loading}
          className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Bottom Row: Filters (shown for table view or when showFilters is true) */}
      {showFilters && (
        <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <FunnelIcon className="w-4 h-4 text-gray-400" />
            <select
              value={selectedStatus}
              onChange={(e) => onStatusChange?.(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              <option value="2xx">2xx Success</option>
              <option value="3xx">3xx Redirect</option>
              <option value="4xx">4xx Client Error</option>
              <option value="5xx">5xx Server Error</option>
            </select>
          </div>

          {/* Search */}
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => onSearchChange?.(e.target.value)}
              placeholder="Search path or IP..."
              className="w-full pl-9 pr-4 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Export Button */}
          {onExport && (
            <button
              onClick={onExport}
              className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 transition-colors"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              Export
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default NginxTrafficHeader;
